/**
 * Tela de login — Isomorphic sign-in-5 com paleta Buckler (dourado + prata metálico).
 * Layout split: formulário branco à esquerda + painel escuro dourado à direita.
 */
import { FormEvent, useState } from 'react';
import axios from 'axios';
import { PiEyeBold, PiEyeSlashBold, PiSpinnerBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

/* ── axios sem baseURL de API — submete para /login no web middleware ── */
const authApi = axios.create({
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});
const csrf = (window as any).__AUTH_PAGE__?.csrfToken ?? '';
if (csrf) authApi.defaults.headers.common['X-CSRF-TOKEN'] = csrf;

/* ─── Campo de formulário ──────────────────────────────────── */
function Field({ label, id, type = 'text', value, onChange, placeholder, autoComplete, error, children }: {
    label: string; id: string; type?: string; value: string; onChange: (v: string) => void;
    placeholder?: string; autoComplete?: string; error?: string; children?: React.ReactNode;
}) {
    return (
        <div>
            <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-gray-700">
                {label}
            </label>
            <div className="relative">
                <input
                    id={id} type={type} value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder} autoComplete={autoComplete}
                    className={cn(
                        'w-full rounded-xl border px-4 py-3 text-sm outline-none transition',
                        'border-gray-200 bg-white text-gray-900 placeholder-gray-400',
                        'focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20',
                        error && 'border-red-400 focus:border-red-400 focus:ring-red-100',
                    )}
                />
                {children}
            </div>
            {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
        </div>
    );
}

/* ─── Recuperação de senha ─────────────────────────────────── */
function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
    const [errorMsg, setErrorMsg] = useState('');

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMsg('');
        try {
            await authApi.post('/login/forgot-password', { email });
            setStatus('sent');
        } catch (err: any) {
            setStatus('error');
            setErrorMsg(err?.response?.data?.message ?? 'Erro ao enviar e-mail.');
        }
    };

    if (status === 'sent') {
        return (
            <div className="text-center">
                <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-[#FDF5DC]">
                    <span className="text-2xl">✉️</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Verifique seu e-mail</h2>
                <p className="mt-2 text-sm text-gray-500">
                    Se o endereço <strong className="text-gray-700">{email}</strong> estiver
                    cadastrado, você receberá um link de recuperação em breve.
                </p>
                <button type="button" onClick={onBack} className="btn-gold mt-6 w-full rounded-xl px-4 py-3 text-sm">
                    Voltar ao login
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <button
                type="button" onClick={onBack}
                className="mb-6 flex items-center gap-1.5 text-sm font-medium text-[#9A7B28] hover:text-[#C9A84C]"
            >
                ← Voltar ao login
            </button>
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">Recuperar senha</h2>
            <p className="mt-1.5 text-sm text-gray-500">
                Digite seu e-mail e enviaremos um link de redefinição.
            </p>
            <div className="mt-6">
                <Field
                    label="E-mail" id="forgot-email" type="email" value={email}
                    onChange={setEmail} placeholder="seu@email.com" autoComplete="email"
                    error={errorMsg}
                />
            </div>
            <button
                type="submit" disabled={status === 'loading' || !email}
                className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm"
            >
                {status === 'loading' && <PiSpinnerBold className="size-4 animate-spin" />}
                Enviar link de recuperação
            </button>
        </form>
    );
}

/* ─── Login rápido (só em ambiente local) ──────────────────── */
type DevProfile = { label: string; role: string; color: string };
type DevUser = { id: number; name: string; email: string; team_name?: string | null };

const DEV_PROFILES: DevProfile[] = [
    { label: 'Super Admin',       role: 'super_admin',          color: '#7c3aed' },
    { label: 'COMEX',             role: 'comex',                color: '#0369a1' },
    { label: 'Financeiro',        role: 'financeiro',           color: '#047857' },
    { label: 'Logística',         role: 'logistica',            color: '#b45309' },
    { label: 'Agente de Carga',   role: 'cargo_agent',          color: '#6b7280' },
    { label: 'Despachante',       role: 'customs_broker',       color: '#6b7280' },
    { label: 'Vendedor',          role: 'salesperson',          color: '#db2777' },
    { label: 'Gestor de Vendas',  role: 'sales_manager',        color: '#c026d3' },
    { label: 'Diretor Comercial', role: 'commercial_director',  color: '#9333ea' },
    { label: 'CFO',               role: 'financial_director',   color: '#0f766e' },
    { label: 'RH',                role: 'hr',                   color: '#be185d' },
    { label: 'Contas a Receber',  role: 'accounts_receivable',  color: '#d97706' },
    { label: 'Contas a Pagar',    role: 'accounts_payable',     color: '#b45309' },
    { label: 'Gerência de Marketing', role: 'marketing_manager', color: '#059669' },
];

function DevQuickLogin() {
    const [loading, setLoading] = useState<string | null>(null);
    const [selectedProfile, setSelectedProfile] = useState<DevProfile | null>(null);
    const [users, setUsers] = useState<DevUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);
    const [filter, setFilter] = useState('');

    const openProfile = async (profile: DevProfile) => {
        setSelectedProfile(profile);
        setUsers([]);
        setFilter('');
        setUsersError(null);
        setLoadingUsers(true);
        try {
            const { data } = await authApi.get('/dev-login/users', { params: { role: profile.role } });
            setUsers(data.data ?? []);
        } catch (err: any) {
            const msg = err?.response?.data?.message;
            setUsersError(msg
                ? `Não foi possível carregar os usuários: ${msg}`
                : 'Não foi possível carregar os usuários deste perfil.');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleDevLogin = async (email: string) => {
        setLoading(email);
        try {
            const { data } = await authApi.post('/dev-login', { email });
            window.location.href = data.redirect ?? '/';
        } catch {
            setLoading(null);
            alert('Erro no dev-login. Verifique se o usuário existe no banco.');
        }
    };

    const filteredUsers = users.filter((u) => {
        if (!filter.trim()) return true;
        const q = filter.trim().toLowerCase();
        return (
            u.name.toLowerCase().includes(q)
            || u.email.toLowerCase().includes(q)
            || (u.team_name?.toLowerCase().includes(q) ?? false)
        );
    });

    return (
        <>
            <div className="mt-6 rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    ⚡ Acesso rápido — ambiente local
                </p>
                <div className="flex flex-wrap gap-2">
                    {DEV_PROFILES.map((profile) => (
                        <button
                            key={profile.role}
                            type="button"
                            onClick={() => openProfile(profile)}
                            disabled={loading !== null}
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-95 disabled:opacity-50"
                            style={{ backgroundColor: profile.color }}
                        >
                            {profile.label}
                        </button>
                    ))}
                </div>
            </div>

            {selectedProfile && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => !loading && setSelectedProfile(null)}
                >
                    <div
                        className="flex max-h-[min(32rem,85vh)] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-b border-gray-100 px-4 py-3">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                                        Login rápido
                                    </p>
                                    <h3 className="text-base font-bold text-gray-900">
                                        {selectedProfile.label}
                                    </h3>
                                    <p className="mt-0.5 text-xs text-gray-500">
                                        Escolha o usuário para entrar
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => !loading && setSelectedProfile(null)}
                                    className="rounded-lg px-2 py-1 text-sm text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                >
                                    ✕
                                </button>
                            </div>
                            {users.length > 5 && (
                                <input
                                    type="search"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    placeholder="Filtrar por nome, e-mail ou equipe…"
                                    className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20"
                                />
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto p-2">
                            {loadingUsers && (
                                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
                                    <PiSpinnerBold className="size-4 animate-spin" />
                                    Carregando…
                                </div>
                            )}
                            {!loadingUsers && usersError && (
                                <p className="px-3 py-8 text-center text-sm text-red-600">{usersError}</p>
                            )}
                            {!loadingUsers && !usersError && filteredUsers.length === 0 && (
                                <p className="px-3 py-8 text-center text-sm text-gray-500">
                                    Nenhum usuário com este perfil no banco.
                                </p>
                            )}
                            {!loadingUsers && !usersError && filteredUsers.map((user) => (
                                <button
                                    key={user.id}
                                    type="button"
                                    disabled={loading !== null}
                                    onClick={() => handleDevLogin(user.email)}
                                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-amber-50 disabled:opacity-50"
                                >
                                    <span
                                        className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                                        style={{ backgroundColor: selectedProfile.color }}
                                    >
                                        {user.name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold text-gray-900">
                                            {user.name}
                                        </span>
                                        <span className="block truncate text-xs text-gray-500">
                                            {user.email}
                                            {user.team_name ? ` · ${user.team_name}` : ''}
                                        </span>
                                    </span>
                                    {loading === user.email ? (
                                        <PiSpinnerBold className="size-4 shrink-0 animate-spin text-amber-600" />
                                    ) : (
                                        <span className="shrink-0 text-xs font-medium text-amber-700">Entrar →</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ─── Formulário de login ──────────────────────────────────── */
type OtpData = { maskedEmail: string; ttlMinutes: number };

function LoginForm({ onForgot, onNeedsOtp }: { onForgot: () => void; onNeedsOtp: (d: OtpData) => void }) {
    const [email, setEmail]       = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading]   = useState(false);
    const [errors, setErrors]     = useState<{ email?: string; password?: string; general?: string }>({});

    const doLogin = async (loginEmail: string, loginPassword: string) => {
        setLoading(true);
        setErrors({});
        try {
            const { data } = await authApi.post('/login', { email: loginEmail, password: loginPassword, remember });
            if (data.needs_otp) {
                onNeedsOtp({ maskedEmail: data.masked_email, ttlMinutes: data.ttl_minutes });
                return;
            }
            window.location.href = data.redirect ?? '/';
        } catch (err: any) {
            const resp = err?.response;
            if (resp?.status === 422) {
                const e = resp.data?.errors ?? {};
                setErrors({ email: e.email?.[0], password: e.password?.[0] });
            } else {
                setErrors({ general: 'Erro de conexão. Tente novamente.' });
            }
            setLoading(false);
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        doLogin(email, password);
    };

    const isLocal = (window as any).__AUTH_PAGE__?.isLocal === true;

    return (
        <form onSubmit={handleSubmit} noValidate>
            <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">Bem-vindo de volta!</h2>
            <p className="mt-1.5 text-sm text-gray-500">
                Acesse sua conta para continuar.
            </p>

            {errors.general && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {errors.general}
                </div>
            )}

            <div className="mt-6 space-y-4">
                <Field
                    label="E-mail" id="email" type="email" value={email}
                    onChange={setEmail} placeholder="seu@bucklerfit.com.br"
                    autoComplete="email" error={errors.email}
                />
                <Field
                    label="Senha" id="password"
                    type={showPass ? 'text' : 'password'}
                    value={password} onChange={setPassword}
                    placeholder="••••••••" autoComplete="current-password"
                    error={errors.password}
                >
                    <button
                        type="button" onClick={() => setShowPass((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        tabIndex={-1}
                    >
                        {showPass ? <PiEyeSlashBold className="size-5" /> : <PiEyeBold className="size-5" />}
                    </button>
                </Field>
            </div>

            {/* Lembrar de mim + Esqueci a senha */}
            <div className="mt-4 flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
                    <input
                        type="checkbox" checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="size-4 rounded border-gray-300 text-[#C9A84C] focus:ring-[#C9A84C]"
                    />
                    Lembrar de mim
                </label>
                <button
                    type="button" onClick={onForgot}
                    className="text-sm font-medium text-[#9A7B28] hover:text-[#C9A84C] transition-colors"
                >
                    Esqueci minha senha
                </button>
            </div>

            {/* Botão entrar com efeito metálico dourado */}
            <button
                type="submit" disabled={loading}
                className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/50 focus:ring-offset-2"
            >
                {loading && <PiSpinnerBold className="size-4 animate-spin" />}
                {loading ? 'Entrando…' : 'Entrar'}
            </button>

            {isLocal && <DevQuickLogin />}
        </form>
    );
}

/* ─── Linha decorativa metálica ────────────────────────────── */
function GoldDivider() {
    return (
        <div
            className="h-px w-24"
            style={{
                background: 'linear-gradient(90deg, transparent 0%, #C9A84C 50%, transparent 100%)',
            }}
        />
    );
}

/* ─── PÁGINA PRINCIPAL ─────────────────────────────────────── */
type Props = { onNeedsOtp: (d: OtpData) => void };

export default function LoginPage({ onNeedsOtp }: Props) {
    const [showForgot, setShowForgot] = useState(false);

    return (
        <div className="flex min-h-screen">

            {/* ── Coluna esquerda: formulário ──────────────── */}
            <div className="flex w-full flex-col justify-between px-6 py-10 sm:px-10 lg:w-[48%] lg:px-16 xl:px-24">
                {/* Logo */}
                <div>
                    <img src="/img/design/Logo_Buckler_Oldblack.png" alt="Buckler Fit" className="h-8" />
                </div>

                {/* Formulário */}
                <div className="mx-auto w-full max-w-md">
                    {showForgot
                        ? <ForgotPasswordForm onBack={() => setShowForgot(false)} />
                        : <LoginForm onForgot={() => setShowForgot(true)} onNeedsOtp={onNeedsOtp} />
                    }
                </div>

                {/* Rodapé */}
                <p className="text-center text-xs text-gray-400">
                    © {new Date().getFullYear()} Buckler Fit · Gestão de Importações
                </p>
            </div>

            {/* ── Coluna direita: painel metálico escuro ────── */}
            <div
                className="relative hidden flex-col items-center justify-center overflow-hidden lg:flex lg:w-[52%]"
                style={{ background: 'linear-gradient(160deg, #0f0d08 0%, #1a1508 40%, #0f0d08 100%)' }}
            >
                {/* Reflexo dourado de fundo */}
                <div className="pointer-events-none absolute inset-0">
                    <div
                        className="absolute left-1/2 top-1/2 size-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
                        style={{ background: 'radial-gradient(circle, #C9A84C 0%, transparent 70%)' }}
                    />
                    <div
                        className="absolute -left-24 bottom-0 size-72 rounded-full opacity-10 blur-2xl"
                        style={{ background: '#D4AF37' }}
                    />
                    <div
                        className="absolute -right-24 top-0 size-72 rounded-full opacity-10 blur-2xl"
                        style={{ background: '#9A7B28' }}
                    />
                </div>

                {/* Borda dourada lateral esquerda */}
                <div
                    className="absolute left-0 top-0 h-full w-px"
                    style={{ background: 'linear-gradient(180deg, transparent 0%, #C9A84C 30%, #E8CC6A 50%, #C9A84C 70%, transparent 100%)' }}
                />

                {/* Conteúdo central */}
                <div className="relative z-10 flex flex-col items-center px-12 text-center">
                    {/* Logo off-white */}
                    <img
                        src="/img/design/Logo_Buckler_OffWhite.png"
                        alt="Buckler Fit"
                        className="mb-10 h-12 opacity-90"
                    />

                    {/* Linha decorativa */}
                    <GoldDivider />

                    {/* Quote */}
                    <blockquote className="mt-8 max-w-sm">
                        <p className="text-2xl font-bold leading-snug" style={{ color: '#E8CC6A' }}>
                            "Queira mais que os outros."
                        </p>
                        <footer className="mt-6">
                            <p className="text-sm font-bold text-white/80">Bruno Parlato</p>
                            <p className="mt-1 text-xs italic text-white/35">
                                "Deve ser ruim p*ra car*lho não ser Buckler."
                            </p>
                        </footer>
                    </blockquote>

                    <GoldDivider />
                </div>
            </div>
        </div>
    );
}
